/**
 * Persistent booking summary — sidebar card on desktop, sticky bottom bar
 * on mobile (with an "expand" toggle that opens the full list as a sheet).
 *
 * The cart is the only durable surface that follows the user across every
 * step of the flow, so it intentionally lives in the page chrome rather
 * than being conditional on the current step.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSalonBooking } from "./SalonBookingContext";
import { formatDuration, formatPrice, pickLocalized } from "./salonTheme";

function CartLine({ palette, service, lang, onRemove }) {
  const { t } = useTranslation();
  return (
    <div
      className="d-flex align-items-start gap-2"
      style={{
        padding: "10px 0",
        borderBottom: palette.border,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="fw-600"
          style={{
            color: palette.text,
            fontSize: "0.92rem",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {pickLocalized(service.name, lang)}
        </div>
        <div
          style={{
            fontSize: "0.78rem",
            color: palette.textMuted,
            marginTop: 2,
          }}
        >
          <i className="las la-clock me-1" aria-hidden="true" />
          {formatDuration(service.duration, lang)}
          {" • "}
          <span
            className="fw-600"
            style={{ color: palette.text, fontVariantNumeric: "tabular-nums" }}
          >
            {service.priceFrom != null && service.price == null
              ? `${t("salonBooking.service.from")} ${formatPrice(service.priceFrom, "AMD", lang)}`
              : formatPrice(service.price ?? service.priceFrom ?? 0, "AMD", lang)}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t("salonBooking.cart.removeAria")}
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "transparent",
          color: palette.textMuted,
          border: palette.border,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.9rem",
          flexShrink: 0,
        }}
      >
        <i className="las la-times" aria-hidden="true" />
      </button>
    </div>
  );
}

/**
 * `onContinue`: parent decides what "Continue" does — usually opens the
 * checkout drawer (Master → Date/Time → Client).
 */
export function BookingCart({ palette, lang, onContinue }) {
  const { t } = useTranslation();
  const {
    cart,
    totalDuration,
    totalPrice,
    hasOpenPriceFrom,
    removeService,
  } = useSalonBooking();
  const [mobileOpen, setMobileOpen] = useState(false);

  const empty = cart.length === 0;
  const priceLabel = empty
    ? ""
    : hasOpenPriceFrom
      ? `${t("salonBooking.service.from")} ${formatPrice(totalPrice, "AMD", lang)}`
      : formatPrice(totalPrice, "AMD", lang);

  /* ------- Desktop sidebar ------- */
  const desktop = (
    <aside
      className="d-none d-lg-block"
      style={{
        position: "sticky",
        top: 24,
        background: palette.surface,
        border: palette.border,
        borderRadius: 18,
        padding: "18px 18px 16px",
        boxShadow: palette.shadowSoft,
      }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: palette.accentSoft,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.1rem",
          }}
          aria-hidden="true"
        >
          <i className="las la-shopping-bag" />
        </span>
        <div className="fw-700" style={{ color: palette.text, fontSize: "1.05rem" }}>
          {t("salonBooking.cart.title")}
        </div>
      </div>

      {empty ? (
        <div
          style={{
            color: palette.textMuted,
            fontSize: "0.9rem",
            padding: "16px 4px",
            textAlign: "center",
          }}
        >
          <i
            className="las la-mouse-pointer"
            style={{
              fontSize: "1.8rem",
              color: palette.accent,
              display: "block",
              marginBottom: 6,
            }}
            aria-hidden="true"
          />
          {t("salonBooking.cart.empty")}
        </div>
      ) : (
        <>
          <div style={{ marginTop: 4 }}>
            {cart.map((s) => (
              <CartLine
                key={s.id}
                palette={palette}
                service={s}
                lang={lang}
                onRemove={() => removeService(s.id)}
              />
            ))}
          </div>

          <div
            className="d-flex align-items-center justify-content-between"
            style={{ paddingTop: 12 }}
          >
            <span style={{ color: palette.textMuted, fontSize: "0.85rem" }}>
              {t("salonBooking.cart.total")}
            </span>
            <span
              className="fw-700"
              style={{
                color: palette.text,
                fontSize: "1.15rem",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {priceLabel}
            </span>
          </div>
          <div
            style={{
              color: palette.textMuted,
              fontSize: "0.78rem",
              marginTop: 2,
            }}
          >
            <i className="las la-clock me-1" aria-hidden="true" />
            {t("salonBooking.cart.approxDuration", {
              duration: formatDuration(totalDuration, lang),
            })}
          </div>

          <button
            type="button"
            onClick={onContinue}
            className="w-100 fw-700"
            style={{
              marginTop: 14,
              background: palette.brandGradient,
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "12px 16px",
              fontSize: "0.95rem",
              cursor: "pointer",
              boxShadow: `0 8px 20px ${palette.accentSoft}`,
            }}
          >
            {t("salonBooking.cart.continue")}
            <i className="las la-arrow-right ms-2" aria-hidden="true" />
          </button>
        </>
      )}
    </aside>
  );

  /* ------- Mobile sticky bar ------- */
  const mobileBar = empty ? null : (
    <>
      <div
        className="d-lg-none"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1050,
          background: palette.surface,
          borderTop: palette.borderStrong,
          padding: "10px 14px",
          boxShadow: "0 -8px 24px rgba(15, 23, 42, 0.12)",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label={t("salonBooking.cart.viewListAria")}
          style={{
            background: palette.accentSoft,
            color: palette.accent,
            border: "none",
            borderRadius: 12,
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          <i className="las la-shopping-bag me-1" aria-hidden="true" />
          {cart.length}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="fw-700"
            style={{
              fontSize: "1rem",
              color: palette.text,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.1,
            }}
          >
            {priceLabel}
          </div>
          <div
            style={{
              fontSize: "0.72rem",
              color: palette.textMuted,
              marginTop: 2,
            }}
          >
            {formatDuration(totalDuration, lang)}
          </div>
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="fw-700"
          style={{
            background: palette.accent,
            color: "#fff",
            border: "none",
            borderRadius: 999,
            padding: "10px 18px",
            cursor: "pointer",
            fontSize: "0.9rem",
            flexShrink: 0,
          }}
        >
          {t("salonBooking.cart.continueShort")}
          <i className="las la-arrow-right ms-1" aria-hidden="true" />
        </button>
      </div>

      {mobileOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setMobileOpen(false)}
          className="d-lg-none"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            zIndex: 1060,
            display: "flex",
            alignItems: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              background: palette.surface,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              padding: "16px 16px 20px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between mb-2"
            >
              <span className="fw-700" style={{ fontSize: "1.05rem", color: palette.text }}>
                {t("salonBooking.cart.title")}
              </span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label={t("salonBooking.cart.closeAria")}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: palette.border,
                  background: palette.surfaceAlt,
                  color: palette.textMuted,
                  cursor: "pointer",
                }}
              >
                <i className="las la-times" aria-hidden="true" />
              </button>
            </div>
            <div>
              {cart.map((s) => (
                <CartLine
                  key={s.id}
                  palette={palette}
                  service={s}
                  lang={lang}
                  onRemove={() => removeService(s.id)}
                />
              ))}
            </div>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ paddingTop: 12 }}
            >
              <span style={{ color: palette.textMuted, fontSize: "0.9rem" }}>
                {t("salonBooking.cart.total")}
              </span>
              <span
                className="fw-700"
                style={{
                  color: palette.text,
                  fontSize: "1.2rem",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {priceLabel}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                onContinue();
              }}
              className="w-100 fw-700"
              style={{
                marginTop: 14,
                background: palette.accent,
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "12px 16px",
                fontSize: "0.95rem",
                cursor: "pointer",
              }}
            >
              {t("salonBooking.cart.continue")}
              <i className="las la-arrow-right ms-2" aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <>
      {desktop}
      {mobileBar}
    </>
  );
}
